import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import CourseQuestionForm from './CourseQuestionForm';
import './CourseQuestionList.css';

const CourseQuestionList = ({ sectionId, sectionTitle }) => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showQuestionForm, setShowQuestionForm] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [deletingQuestionId, setDeletingQuestionId] = useState(null);

    useEffect(() => {
        fetchQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionId]);

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/api/course-questions/section/${sectionId}`, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setQuestions(data);
            } else {
                setError('Failed to fetch questions');
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            setError('Network error while fetching questions');
        } finally {
            setLoading(false);
        }
    };

    const handleQuestionSaved = (savedQuestion) => {
        if (editingQuestion) {
            // Update existing question
            setQuestions(prev => 
                prev.map(q => q.id === savedQuestion.id ? savedQuestion : q)
            );
        } else {
            // Add new question
            setQuestions(prev => [...prev, savedQuestion]);
        }
        setShowQuestionForm(false);
        setEditingQuestion(null);
    };

    const handleEditQuestion = (question) => {
        setEditingQuestion(question);
        setShowQuestionForm(true);
    };

    const handleDeleteQuestion = async (questionId) => {
        if (!window.confirm('Are you sure you want to delete this question?')) {
            return;
        }

        try {
            setDeletingQuestionId(questionId);
            const response = await fetch(`${API_URL}/api/course-questions/${questionId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                setQuestions(prev => prev.filter(q => q.id !== questionId));
            } else {
                setError('Failed to delete question');
            }
        } catch (error) {
            console.error('Error deleting question:', error);
            setError('Network error while deleting question');
        } finally {
            setDeletingQuestionId(null);
        }
    };

    const handleCancelForm = () => {
        setShowQuestionForm(false);
        setEditingQuestion(null);
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'EASY': return 'difficulty-easy';
            case 'MEDIUM': return 'difficulty-medium';
            case 'HARD': return 'difficulty-hard';
            case 'EXPERT': return 'difficulty-expert';
            default: return 'difficulty-easy';
        }
    };

    if (loading) {
        return (
            <div className="course-question-list">
                <div className="loading">Loading questions...</div>
            </div>
        );
    }

    return (
        <div className="course-question-list">
            <div className="question-list-header">
                <h3>Questions for Section: {sectionTitle}</h3>
                <button 
                    className="btn-add-question"
                    onClick={() => setShowQuestionForm(true)}
                >
                    + Add Question
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {showQuestionForm && (
                <CourseQuestionForm
                    sectionId={sectionId}
                    onQuestionSaved={handleQuestionSaved}
                    onCancel={handleCancelForm}
                    editingQuestion={editingQuestion}
                />
            )}

            {questions.length === 0 ? (
                <div className="no-questions">
                    <p>No questions added yet. Click "Add Question" to get started!</p>
                </div>
            ) : (
                <div className="questions-grid">
                    {questions.map((question) => (
                        <div key={question.id} className="question-card">
                            <div className="question-header">
                                <h4 className="question-title">
                                    {question.title}
                                    {question.isPremium && (
                                        <span className="premium-badge">Premium</span>
                                    )}
                                </h4>
                                <div className="question-meta">
                                    <span className={`difficulty-badge ${getDifficultyColor(question.difficultyLevel)}`}>
                                        {question.difficultyLevel}
                                    </span>
                                    <span className="question-order">#{question.questionOrder}</span>
                                </div>
                            </div>

                            <div className="question-content">
                                <p className="question-description">{question.description}</p>
                                
                                {question.problemStatement && (
                                    <div className="question-section">
                                        <strong>Problem Statement:</strong>
                                        <p>{question.problemStatement}</p>
                                    </div>
                                )}

                                {question.videoLink && (
                                    <div className="question-section">
                                        <strong>Video:</strong>
                                        <a href={question.videoLink} target="_blank" rel="noopener noreferrer">
                                            Watch Video
                                        </a>
                                    </div>
                                )}

                                {question.solution && (
                                    <div className="question-section">
                                        <strong>Solution:</strong>
                                        <p>{question.solution}</p>
                                    </div>
                                )}

                                {question.solutionCode && (
                                    <div className="question-section">
                                        <strong>Solution Code:</strong>
                                        <pre className="code-block">{question.solutionCode}</pre>
                                    </div>
                                )}

                                {question.testCases && (
                                    <div className="question-section">
                                        <strong>Test Cases:</strong>
                                        <pre className="code-block">{question.testCases}</pre>
                                    </div>
                                )}

                                {question.constraints && (
                                    <div className="question-section">
                                        <strong>Constraints:</strong>
                                        <p>{question.constraints}</p>
                                    </div>
                                )}

                                {question.examples && (
                                    <div className="question-section">
                                        <strong>Examples:</strong>
                                        <p>{question.examples}</p>
                                    </div>
                                )}

                                {question.relatedTopics && (
                                    <div className="question-section">
                                        <strong>Related Topics:</strong>
                                        <p>{question.relatedTopics}</p>
                                    </div>
                                )}

                                {question.followUp && (
                                    <div className="question-section">
                                        <strong>Follow-up Questions:</strong>
                                        <p>{question.followUp}</p>
                                    </div>
                                )}

                                <div className="question-stats">
                                    {question.timeLimitSeconds && (
                                        <span className="stat">Time: {question.timeLimitSeconds}s</span>
                                    )}
                                    {question.memoryLimitMB && (
                                        <span className="stat">Memory: {question.memoryLimitMB}MB</span>
                                    )}
                                    {question.submissionsCount !== null && (
                                        <span className="stat">Submissions: {question.submissionsCount}</span>
                                    )}
                                    {question.successRate !== null && question.successRate !== undefined && (
                                        <span className="stat">Success Rate: {Number(question.successRate).toFixed(1)}%</span>
                                    )}
                                </div>
                            </div>

                            <div className="question-actions">
                                <button 
                                    className="btn-edit"
                                    onClick={() => handleEditQuestion(question)}
                                >
                                    Edit
                                </button>
                                <button 
                                    className="btn-delete"
                                    onClick={() => handleDeleteQuestion(question.id)}
                                    disabled={deletingQuestionId === question.id}
                                >
                                    {deletingQuestionId === question.id ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CourseQuestionList; 
